import type { KidOverviewDataItem } from '../../../../api/kids/types';

/**
 * Compute target and comparison version from historical overview data for the review page.
 * Target (left) = baseline e.g. 4.1.0; Comparison (right) = version under review = latest.
 */
export function getReviewVersionParams(
  historicalData: Record<string, KidOverviewDataItem[]> | undefined,
): { targetVersion: string; compareVersion: string } | null {
  if (!historicalData) return null;

  const versionSet = new Set<string>();
  Object.values(historicalData).forEach((items) => {
    items.forEach((item) => {
      if (item.version_number) versionSet.add(item.version_number);
    });
  });

  const allVersions = Array.from(versionSet).sort((a, b) => {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;
      if (bVal !== aVal) return bVal - aVal;
    }
    return 0;
  });

  if (allVersions.length === 0) return null;

  const comparisonVersion = allVersions[0];

  const versionsByMinor = new Map<string, string[]>();
  allVersions.forEach((version) => {
    const parts = version.split('.');
    if (parts.length >= 2) {
      const minorKey = `${parts[0]}.${parts[1]}`;
      if (!versionsByMinor.has(minorKey)) versionsByMinor.set(minorKey, []);
      versionsByMinor.get(minorKey)!.push(version);
    }
  });

  const latestMinorGroup = Array.from(versionsByMinor.keys()).sort((a, b) => {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;
      if (bVal !== aVal) return bVal - aVal;
    }
    return 0;
  })[0];

  const latestMinorVersions = latestMinorGroup ? versionsByMinor.get(latestMinorGroup) ?? [] : [];
  let targetVersion: string | null = null;

  if (latestMinorVersions.length > 0 && latestMinorGroup) {
    const minorParts = latestMinorGroup.split('.').map(Number);
    const sortedMinorVersions = [...latestMinorVersions].sort((a, b) => {
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aVal = aParts[i] || 0;
        const bVal = bParts[i] || 0;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    });
    targetVersion = sortedMinorVersions[0];
    const baseVersion = `${minorParts[0]}.${minorParts[1]}.0`;
    if (sortedMinorVersions.includes(baseVersion)) targetVersion = baseVersion;
  }

  if (!targetVersion || !comparisonVersion) return null;
  return { targetVersion, compareVersion: comparisonVersion };
}
